import React from 'react';
import { buildStaticUrl } from '../../config/env';
import { useStore } from '../../stores';
import { getModelDisplayName } from '../../utils/modelDisplay';

export type AIModel = 'gemini' | 'deepseek';

interface ModelSelectorProps {
  selectedModel: AIModel;
  onModelChange: (model: AIModel) => void;
  isLocked?: boolean;
}

/**
 * AI 模型选择器组件
 * 使用用户头像和圆点 checkbox 进行选择
 */
export const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  onModelChange,
  isLocked = false
}) => {
  const { user } = useStore();

  // 获取模型头像
  const getModelAvatar = (model: AIModel): string => {
    if (model === 'gemini' && user?.ai_avatar_gemini) {
      return buildStaticUrl(user.ai_avatar_gemini);
    } else if (model === 'deepseek' && user?.ai_avatar_deepseek) {
      return buildStaticUrl(user.ai_avatar_deepseek);
    }
    
    // 默认头像
    if (model === 'gemini') {
      return buildStaticUrl('/uploads/xid_teacher1.jpg');
    } else {
      return buildStaticUrl('/uploads/xid_teacher2.jpg');
    }
  };

  if (isLocked) {
    return (
      <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src={getModelAvatar(selectedModel)}
            alt={getModelDisplayName(selectedModel)}
            className="w-6 h-6 rounded-full object-cover"
          />
          <span>{getModelDisplayName(selectedModel)}</span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">已锁定</span>
      </div>
    );
  }

  return (
    <div className="flex gap-4 justify-center">
      {/* 模型 A (Gemini) */}
      <button
        onClick={() => onModelChange('gemini')}
        disabled={isLocked}
        className="flex flex-col items-center gap-2 p-3 rounded-lg transition-all hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed"
      >
        <div className="relative">
          <img
            src={getModelAvatar('gemini')}
            alt="模型 A"
            className={`w-16 h-16 rounded-full object-cover border-2 transition-all ${
              selectedModel === 'gemini'
                ? 'border-blue-500 opacity-100'
                : 'border-gray-300 dark:border-gray-600 opacity-50'
            }`}
          />
          {/* 圆点 checkbox */}
          <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center transition-all ${
            selectedModel === 'gemini'
              ? 'bg-blue-500'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}>
            {selectedModel === 'gemini' && (
              <div className="w-3 h-3 rounded-full bg-white"></div>
            )}
          </div>
        </div>
        <span className={`text-sm font-medium transition-colors ${
          selectedModel === 'gemini'
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400'
        }`}>
          模型 A
        </span>
      </button>

      {/* 模型 B (DeepSeek) */}
      <button
        onClick={() => onModelChange('deepseek')}
        disabled={isLocked}
        className="flex flex-col items-center gap-2 p-3 rounded-lg transition-all hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed"
      >
        <div className="relative">
          <img
            src={getModelAvatar('deepseek')}
            alt="模型 B"
            className={`w-16 h-16 rounded-full object-cover border-2 transition-all ${
              selectedModel === 'deepseek'
                ? 'border-blue-500 opacity-100'
                : 'border-gray-300 dark:border-gray-600 opacity-50'
            }`}
          />
          {/* 圆点 checkbox */}
          <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center transition-all ${
            selectedModel === 'deepseek'
              ? 'bg-blue-500'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}>
            {selectedModel === 'deepseek' && (
              <div className="w-3 h-3 rounded-full bg-white"></div>
            )}
          </div>
        </div>
        <span className={`text-sm font-medium transition-colors ${
          selectedModel === 'deepseek'
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400'
        }`}>
          模型 B
        </span>
      </button>
    </div>
  );
};
